import React, { useState, useEffect } from 'react';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  label?: string;
  required?: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  placeholder = '+7 (___) ___-__-__',
  disabled = false,
  className = '',
  error = false,
  label = 'Номер телефона',
  required = false
}) => {
  const [phoneNumber, setPhoneNumber] = useState(value);

  useEffect(() => {
    setPhoneNumber(value);
  }, [value]);

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    
    if (!digits || digits === '7') return '+7';
    
    const cleanDigits = digits.startsWith('7') ? digits.slice(1) : digits;
    
    let formatted = '+7';
    if (cleanDigits.length > 0) {
      formatted += ` (${cleanDigits.slice(0, 3)}`;
      if (cleanDigits.length > 3) {
        formatted += `) ${cleanDigits.slice(3, 6)}`;
        if (cleanDigits.length > 6) {
          formatted += `-${cleanDigits.slice(6, 8)}`;
          if (cleanDigits.length > 8) {
            formatted += `-${cleanDigits.slice(8, 10)}`;
          }
        }
      }
    }
    return formatted;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    if (value.length < phoneNumber.length) {
      setPhoneNumber(value);
      onChange(value);
      return;
    }

    const digits = value.replace(/\D/g, '');
    
    if (!digits) {
      const newValue = '+7';
      setPhoneNumber(newValue);
      onChange(newValue);
      return;
    }

    const truncatedDigits = digits.slice(0, 11);
    const formatted = formatPhoneNumber(truncatedDigits);
    setPhoneNumber(formatted);
    onChange(formatted);
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace, Delete, Tab, Escape, Enter
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'].includes(e.key)) {
      return;
    }
    
    // Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
      return;
    }
    
    // Home, End, Arrow keys
    if (['Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      return;
    }

    // Разрешаем только цифры
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }

    const digits = phoneNumber.replace(/\D/g, '');
    if (digits.length >= 11 && !/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 11);
    const formatted = formatPhoneNumber(digits);
    setPhoneNumber(formatted);
    onChange(formatted);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <input
        type="tel"
        value={phoneNumber}
        onChange={handlePhoneChange}
        onKeyDown={handlePhoneKeyDown}
        onPaste={handlePhonePaste}
        placeholder={placeholder}
        disabled={disabled}
        className={`
          w-full px-4 py-3 border rounded-lg text-lg font-medium
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
          transition-all duration-200
          ${error 
            ? 'border-red-500 focus:ring-red-500' 
            : 'border-gray-300 focus:border-blue-500'
          }
          ${disabled 
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
            : 'bg-white text-gray-900'
          }
        `}
        inputMode="numeric"
        autoComplete="tel"
        maxLength={18}
      />
    </div>
  );
};

export default PhoneInput;
